import { z } from "zod";
import { NOTIFICATION_TYPES } from "@/generated/contracts";
import { propertyShareRequestSchema } from "../../portfolio/api/schemas/share.schemas";

export enum NotificationTypeEnum {
  PROPERTY_SHARE = "property_share",
  PROPERTY_SHARE_ACCEPTED = "property_share_accepted",
  CONNECTION_REQUEST = "connection_request",
  AL_CHECK_IN_SUBMITTED = "al_check_in_submitted",
  LISTING_AUTO_DEACTIVATED = "listing_auto_deactivated",
  LISTING_MATCH = "listing_match",
  LISTING_PRICE_CHANGE = "listing_price_change",
  CONVERSATION_UNANSWERED = "conversation_unanswered",
  LISTING_SUGGESTIONS = "listing_suggestions",
  CHARGE_DUE = "charge_due",
  CHARGE_OVERDUE = "charge_overdue",
  CHARGE_AUTO_PAID = "charge_auto_paid",
  CHARGE_AUTO_PAY_FAILED = "charge_auto_pay_failed",
}

export const unviewedCountSchema = z.object({
  count: z.number().min(0),
});

export type UnviewedCount = z.infer<typeof unviewedCountSchema>;

const propertyShareResourceSchema = propertyShareRequestSchema.extend({
  type: z.literal("PropertyShare"),
  fromUser: z.object({
    id: z.string().uuid().nullable(),
    name: z.string().nullable(),
    avatar: z.string().nullable().optional(),
  }),
  property: z.object({
    id: z.string().uuid().nullable(),
    title: z.string().nullable(),
    thumbnail: z.string().nullable().optional(),
  }),
});

const resourceSchema = z.discriminatedUnion("type", [
  propertyShareResourceSchema,
]);

export const notificationSchema = z.object({
  id: z.string().uuid(),
  notificationType: z.enum(NOTIFICATION_TYPES),
  resourceId: z.string().uuid().optional().nullable(),
  userId: z.string().uuid(),
  title: z.string().optional().nullable(),
  message: z.string().optional().nullable(),
  data: z.string().optional().nullable(), // JSON payload for extra context
  viewedAt: z.string().datetime().optional().nullable(),
  interactedAt: z.string().datetime().optional().nullable(),
  deletedAt: z.string().datetime().optional().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  resource: resourceSchema.optional().nullable(),
});

export type NotificationType = z.infer<typeof notificationSchema>;

const parseData = <T>(raw: string | null | undefined): Partial<T> => {
  if (!raw) return {};
  try {
    return JSON.parse(raw) as Partial<T>;
  } catch {
    return {};
  }
};

export type ConnectionRequestData = {
  fromUserId?: string;
  senderName?: string;
  senderAvatar?: string | null;
};
export const parseConnectionData = (raw: string | null | undefined) =>
  parseData<ConnectionRequestData>(raw);

export type AlCheckInSubmittedData = {
  propertyId?: string;
  propertyTitle?: string;
  image?: string | null;
  eventId?: string;
  checkInId?: string;
};
export const parseAlCheckInData = (raw: string | null | undefined) =>
  parseData<AlCheckInSubmittedData>(raw);

export type ListingAutoDeactivatedData = {
  listingId?: string;
  listingTitle?: string;
  image?: string | null;
};
export const parseListingAutoDeactivatedData = (
  raw: string | null | undefined,
) => parseData<ListingAutoDeactivatedData>(raw);

export type ListingMatchData = {
  listingId?: string;
  listingSlug?: string;
  listingTitle?: string;
  matchedListingId?: string;
  matchedListingTitles?: string[];
  direction?: "dropped" | "rose";
  fromPrice?: string | null;
  toPrice?: string | null;
  image?: string | null;
  matchedImage?: string | null;
};
export const parseListingMatchData = (raw: string | null | undefined) =>
  parseData<ListingMatchData>(raw);

export type ConversationUnansweredData = {
  conversationId?: string;
  listingId?: string;
  listingTitle?: string;
  senderName?: string;
  image?: string | null;
};
export const parseConversationUnansweredData = (
  raw: string | null | undefined,
) => parseData<ConversationUnansweredData>(raw);

export type ListingSuggestionsData = {
  conversationId?: string;
  listingId?: string;
  listingTitle?: string;
  suggestedListingIds?: string[];
  suggestedListingTitles?: string[];
  image?: string | null;
};
export const parseListingSuggestionsData = (raw: string | null | undefined) =>
  parseData<ListingSuggestionsData>(raw);

export type ChargeNotificationData = {
  chargeId?: string;
  propertyId?: string;
  amount?: string;
  dueDate?: string;
  estimated?: boolean;
  paymentType?: string;
  direction?: "income" | "expense";
};
export const parseChargeData = (raw: string | null | undefined) =>
  parseData<ChargeNotificationData>(raw);

export type PropertyUnfollowedData = {
  propertyId?: string;
  unfollowedByUserId?: string;
  unfollowedByUserName?: string;
};
export const parsePropertyUnfollowedData = (raw: string | null | undefined) =>
  parseData<PropertyUnfollowedData>(raw);
